import { CpsNotificationProps } from '@cs-cms/types';
import React, { useState } from 'react';
import { NotificationContext } from '../contexts/NotificationContext';
import { NotificationCenterItem } from '../types/NotificationCenterItem';

interface NotificationContextProviderProps {
  children: React.ReactNode;
  renderToast?: (props: CpsNotificationProps, onClose: () => void) => React.ReactNode;
}

export const NotificationContextProvider: React.FC<NotificationContextProviderProps> = ({ children, renderToast }) => {
  const [toastNotificationProps, setToastNotificationProps] = useState<CpsNotificationProps | null>(null);
  const [centerNotifications, setCenterNotifications] = useState<NotificationCenterItem[]>([]);

  const showToast = (props: Omit<CpsNotificationProps, 'show'>): void => {
    setToastNotificationProps({ ...props, show: true });
  };

  const addCenterNotification = (notification: NotificationCenterItem): void => {
    setCenterNotifications(prev => [notification, ...prev]);
  };

  const updateCenterNotification = (notificationId: string, updates: Partial<NotificationCenterItem>): void => {
    setCenterNotifications(prev =>
      prev.map(notification => {
        if (notification.id === notificationId) {
          return {
            ...notification,
            ...updates,
            progress:
              notification.progress && updates.progress
                ? { ...notification.progress, ...updates.progress }
                : updates.progress || notification.progress
          };
        }
        return notification;
      })
    );
  };

  const removeCenterNotification = (notificationId: string): void => {
    setCenterNotifications(prev => prev.filter(x => x.id !== notificationId));
  };

  const clearAllCenterNotifications = (): void => {
    setCenterNotifications([]);
  };

  const unreadCenterCount = centerNotifications.filter(notification => !notification.read).length;

  const markAllAsRead = (): void => {
    if (unreadCenterCount > 0) {
      setCenterNotifications(prev => prev.map(x => ({ ...x, read: true })));
    }
  };

  return (
    <NotificationContext.Provider
      value={{
        showToast,
        toastNotification: toastNotificationProps,
        centerNotifications,
        addCenterNotification,
        updateCenterNotification,
        removeCenterNotification,
        clearAllCenterNotifications,
        markAllAsRead,
        unreadCenterCount
      }}
    >
      {children}
      {toastNotificationProps && renderToast?.(toastNotificationProps, () => setToastNotificationProps(null))}
    </NotificationContext.Provider>
  );
};
